import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { apiCallStatus, popupTypes } from 'helpers/constants';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { translate } from 'helpers/translateHelper';
import { Col, Container, Row } from 'react-bootstrap';
import { GeneralInput } from 'sharedComponents/GeneralInput';
import { hierarchyLocationModalTypes, NODE_TYPE, VALID_TEXT_REGEX } from './constants';
import { LocationHierarchyDropdown } from 'sharedComponents/LocationHierarchyDropdown';
import { IconCancel } from 'svgIcons/MotionPortalIcons';
import colors from 'theme/_colors.scss';

const HierarchyLocationModal = ({
    show,
    close,
    loadingStatus,
    type,
    selectedNode,
    parentNode,
    handleRenameNode,
    handleDeleteNode,
    handleMoveNode,
    hierarchyData,
    organizations,
}) => {

    const [renameValue, setRenameValue] = useState(selectedNode?.name || '');
    const [moveToDestination, setMoveToDestination] = useState(null);
    const organizationID = organizations?.[0]?.organizationID;
    const isRenameValueInvalid = renameValue && !VALID_TEXT_REGEX.test(renameValue);

    // Check if the new name already exists among sibling locations (same level)
    const isDuplicateAtSameLevel = useMemo(() => {
        if (!renameValue || type !== hierarchyLocationModalTypes.RENAME) { return false; }
        const trimmed = renameValue.trim();
        return (parentNode?.children || [])
            .filter(child => child.type === NODE_TYPE.LOCATION && child.id !== selectedNode?.id)
            .some(child => child.name === trimmed);
    }, [renameValue, parentNode, selectedNode, type]);

    useEffect(() => {
        if (show) {
            if (type === hierarchyLocationModalTypes.RENAME) {
                setRenameValue(selectedNode?.name || '');
            }
            if (type === hierarchyLocationModalTypes.MOVE) {
                setMoveToDestination(null);
            }
        }
    }, [show, selectedNode?.name, type]);

    const handleSubmit = () => {
        if (type === hierarchyLocationModalTypes.RENAME) {
            if (!renameValue || isRenameValueInvalid || isDuplicateAtSameLevel) { return; }
            handleRenameNode(selectedNode?.id, renameValue);
            close();
        } else if (type === hierarchyLocationModalTypes.DELETE) {
            handleDeleteNode(selectedNode?.id);
            close();
        } else {
            if (!moveToDestination) { return; }
            handleMoveNode(selectedNode?.id, moveToDestination.id);
            close();
        }
    };

    const getTitle = () => {
        switch (type) {
            case hierarchyLocationModalTypes.DELETE:
                return translate('ABB.Powertrain.Frontend.deleteLocationLabel');
            case hierarchyLocationModalTypes.RENAME:
                return translate('ABB.Powertrain.Frontend.renameLocationLabel');
            default:
                return translate('ABB.Powertrain.Frontend.moveToLocationLabel');
        }
    };

    const getAcceptButtonText = () => {
        switch (type) {
            case hierarchyLocationModalTypes.DELETE:
                return translate('ABB.Powertrain.Frontend.deleteLabel');
            case hierarchyLocationModalTypes.RENAME:
                return translate('ABB.Powertrain.Frontend.renameLabel');
            default:
                return translate('ABB.Powertrain.Frontend.moveToLabel');
        }
    };

    const handleLocationChange = useCallback((nodeData) => {
        if (!nodeData || !nodeData.id) {
            setMoveToDestination(null);
            return;
        }
        setMoveToDestination(nodeData);
    }, [setMoveToDestination]);

    return <GeneralDialog
        show={show}
        close={close}
        title={getTitle()}
        modalClassName='hierarchy-location-dialog'
        backdropClassName='hierarchy-location-backdrop'
        {...type === hierarchyLocationModalTypes.DELETE ? { notificationType: popupTypes.INFO } : {}}
        cancelButtonProps={{
            text: translate('ABB.Powertrain.Frontend.cancelLabel'),
        }}
        acceptButtonProps={{
            text: getAcceptButtonText(),
            onClick: () => handleSubmit(),
            disabled: loadingStatus === apiCallStatus.LOADING ||
                type === hierarchyLocationModalTypes.MOVE && !moveToDestination ||
                type === hierarchyLocationModalTypes.RENAME && (!renameValue || renameValue === selectedNode?.name || isRenameValueInvalid || isDuplicateAtSameLevel)
        }}
        closeButton={true}
    >
        <Container fluid className='gx-0'>
            <LoadingStatusWrapper
                useCustomAnchor
                transparent
                loadingStatus={loadingStatus}
            >
                {type === hierarchyLocationModalTypes.DELETE &&
                    <Row className='info-text-row gx-0'>
                        <Col className='delete-msg'>{translate('ABB.Powertrain.Frontend.deleteLocationDialogMessage')}</Col>
                    </Row>
                }
                {type === hierarchyLocationModalTypes.RENAME &&
                    <>
                        <div className={`location-input-wrapper${isRenameValueInvalid || isDuplicateAtSameLevel ? ' has-error' : ''}`}>
                            <GeneralInput
                                className={`location-input${isRenameValueInvalid || isDuplicateAtSameLevel ? ' location-input-error' : ''}`}
                                placeholder={translate('ABB.Powertrain.Frontend.renameLocationNameLabel')}
                                onChange={(value) => setRenameValue(value || '')}
                                value={renameValue || ''}
                                maxLength={50}
                                label={translate('ABB.Powertrain.Frontend.renameLocationNameLabel')}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && renameValue && renameValue !== selectedNode?.name && !isRenameValueInvalid && !isDuplicateAtSameLevel) {
                                        handleSubmit();
                                    }
                                }}
                            />
                            {(isRenameValueInvalid || isDuplicateAtSameLevel) &&
                                <div className='location-input-error-icon'>
                                    <IconCancel
                                        showCircle={true}
                                        color={colors.white}
                                        fillColor={colors.pomegranate}
                                        viewBox='0 0 50 50'
                                        width={18}
                                        height={18}
                                    />
                                </div>
                            }
                        </div>
                        {isRenameValueInvalid &&
                            <div className='location-name-error'>
                                {translate('ABB.Powertrain.Frontend.locationNameSpecialCharErrorMessage')}
                            </div>
                        }
                        {!isRenameValueInvalid && isDuplicateAtSameLevel &&
                            <div className='location-name-error'>
                                {translate('ABB.Powertrain.Frontend.locationNameExistsErrorMessage')}
                            </div>
                        }
                    </>
                }
                {type === hierarchyLocationModalTypes.MOVE &&
                    <div className='move-location-container'>
                        <GeneralInput
                            className='location-input'
                            placeholder={translate('ABB.Powertrain.Frontend.currentParentNodeLabel')}
                            value={parentNode?.name || ''}
                            label={translate('ABB.Powertrain.Frontend.currentParentNodeLabel')}
                            disabled={true}
                        />
                        <div className='destination-content'>
                            <label>{translate('ABB.Powertrain.Frontend.destinationNodeLabel')}</label>
                            <LocationHierarchyDropdown
                                hierarchyData={hierarchyData}
                                selectedSite={{ siteId: selectedNode?.data?.siteId }}
                                selectedLocation={moveToDestination}
                                organizationId={organizationID}
                                onSelect={handleLocationChange}
                                placeholder={translate('ABB.Powertrain.Frontend.select')}
                                isForEditHierarchy={true}
                                parentNodeId={parentNode?.id}
                                selectedNodeId={selectedNode?.id}
                            />
                            <span>{translate('ABB.Powertrain.Frontend.moveLocationDescription')}</span>
                        </div>
                    </div>
                }
            </LoadingStatusWrapper>
        </Container>
    </GeneralDialog>;
};

HierarchyLocationModal.propTypes = {
    show: PropTypes.bool.isRequired,
    close: PropTypes.func.isRequired,
    loadingStatus: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    selectedNode: PropTypes.object,
    parentNode: PropTypes.object,
    handleRenameNode: PropTypes.func,
    handleDeleteNode: PropTypes.func,
    handleMoveNode: PropTypes.func,
    hierarchyData: PropTypes.object,
    organizations: PropTypes.array
};

export default HierarchyLocationModal;
